/**
 * Exact integer apportionment — the largest-remainder method.
 *
 * Splits a whole number of units across claimants in proportion to their
 * weights, with the result summing to EXACTLY the requested total. Every mineral
 * or element the factory divides goes through here.
 *
 * Written once and shared deliberately. This same split is needed by strip
 * mining, by dissociation, and anywhere else a scarce pool is divided; the
 * Phase 8 harness found a bug caused by hand-rolling a second version that
 * walked claimants in array order until a budget ran out, which quietly gave
 * everything to whichever claimant sorted first. Proportional division is easy
 * to get subtly wrong and the errors are invisible until an economy is skewed
 * hours in.
 *
 * Naive rounding is not an alternative: floor() leaks units and round() invents
 * them, and either one repeated thousands of times per session destroys the
 * conservation guarantees the whole simulation rests on.
 */

/**
 * @param keys      claimants, in a FIXED order — ties are broken by this order,
 *                  so it must not depend on iteration of an unordered
 *                  collection (spec §11 rule 3)
 * @param weightOf  each claimant's share, non-negative
 * @param total     units to distribute
 * @param capOf     optional per-claimant ceiling; a claimant is never given more
 *                  than this, and the units it cannot take go to the others
 * @returns amounts summing to exactly `min(total, sum of caps)`
 */
export function apportion<K extends string>(
  keys: readonly K[],
  weightOf: (key: K) => number,
  total: number,
  capOf?: (key: K) => number,
): Record<K, number> {
  const result = {} as Record<K, number>;
  for (const key of keys) result[key] = 0;
  if (total <= 0) return result;

  const cap = (key: K): number => (capOf === undefined ? Number.MAX_SAFE_INTEGER : capOf(key));

  let weightSum = 0;
  for (const key of keys) weightSum += Math.max(0, weightOf(key));
  if (weightSum <= 0) return result;

  // Floor share for each claimant, plus the fractional part it lost.
  let allocated = 0;
  const remainders: Array<{ key: K; remainder: number }> = [];

  for (const key of keys) {
    const weight = Math.max(0, weightOf(key));
    const numerator = weight * total;
    const share = Math.min(Math.floor(numerator / weightSum), cap(key));
    result[key] = share;
    allocated += share;
    // Only actual claimants are eligible for the leftover below. A zero-weight
    // key asked for nothing, and handing it another claimant's overflow would
    // put material somewhere nobody requested it.
    if (weight > 0) remainders.push({ key, remainder: numerator % weightSum });
  }

  // Hand out what flooring lost, largest fractional part first. Ties go to the
  // earlier key, so the outcome is identical on every machine.
  const index = new Map(keys.map((key, i) => [key, i]));
  remainders.sort(
    (a, b) =>
      b.remainder - a.remainder ||
      (index.get(a.key) ?? 0) - (index.get(b.key) ?? 0),
  );

  let leftover = total - allocated;

  // Repeated passes, one unit per claimant per pass, in remainder order.
  //
  // Passes rather than a single sweep because a claimant sitting at its cap
  // cannot absorb its share and those units have to find another home — and the
  // overflow can be far larger than the number of claimants, so a loop bounded
  // by claimant count silently returns less than the requested total. Each pass
  // places at least one unit or breaks, so this terminates in at most `leftover`
  // passes, and one unit at a time keeps the spill spread rather than dumped on
  // whichever claimant happens to sort first.
  while (leftover > 0) {
    let placed = false;
    for (const entry of remainders) {
      if (leftover <= 0) break;
      if (result[entry.key] >= cap(entry.key)) continue;
      result[entry.key] += 1;
      leftover -= 1;
      placed = true;
    }
    if (!placed) break;
  }

  return result;
}
